import React, { useEffect, useMemo, useState } from "react";
import {
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    Download,
    Eye,
    FileSpreadsheet,
    RefreshCcw,
    Search,
    Send,
    Upload,
    Warehouse,
    ArrowLeft,
} from "lucide-react";
import { warehouseService } from "../../../services/warehouse.service";
import { importService } from "../../../services/import.service";
import { productsService } from "../../../services/products.service";
import { appAlert } from "../../../utils/appPopup";
import { useNavigate } from "react-router-dom";

const BORDER = "#f2c6cc";

function Card({ children, className = "" }) {
    return (
        <div
            className={"rounded-[20px] border bg-white shadow-sm " + className}
            style={{ borderColor: BORDER }}
        >
            {children}
        </div>
    );
}

function SelectLike({ value, onChange, placeholder, options = [], className = "" }) {
    return (
        <div className={"relative h-[44px] " + className}>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-full w-full appearance-none rounded-[12px] border border-[#f2c6cc] bg-white px-4 pr-10 text-[13px] text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
            >
                <option value="">{placeholder}</option>
                {options.map((o) => (
                    <option key={o.value ?? o.id ?? o} value={o.value ?? o.id ?? o}>
                        {o.label ?? o.name ?? o}
                    </option>
                ))}
            </select>
            <ChevronDown
                size={16}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
        </div>
    );
}

function ActionButton({
    children,
    onClick,
    variant = "primary",
    disabled = false,
    className = "",
    type = "button",
}) {
    const styles =
        variant === "primary"
            ? "bg-[#d86a78] text-white hover:bg-[#c95b69]"
            : variant === "soft"
                ? "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff3f5]"
                : "bg-slate-900 text-white hover:bg-slate-800";

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={
                "inline-flex items-center justify-center gap-2 rounded-[12px] px-4 py-2.5 text-[13px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                styles +
                " " +
                className
            }
        >
            {children}
        </button>
    );
}

function SummaryCard({ icon: Icon, title, value, subtitle }) {
    return (
        <Card className="p-5">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        {title}
                    </div>
                    <div className="mt-2 text-[28px] font-bold text-slate-800">{value}</div>
                    <div className="mt-1 text-[12px] text-slate-500">{subtitle}</div>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-[#fff1f3] text-[#d86a78]">
                    <Icon size={22} />
                </div>
            </div>
        </Card>
    );
}

function StatusPill({ value }) {
    const status = String(value || "").toLowerCase();

    const cls =
        status === "valid" ||
            status === "success" ||
            status === "imported" ||
            status === "assigned"
            ? "bg-emerald-50 text-emerald-600"
            : status === "pending" || status === "preview"
                ? "bg-amber-50 text-amber-700"
                : status === "not assigned"
                    ? "bg-slate-100 text-slate-600"
                    : "bg-rose-50 text-rose-600";

    return (
        <span
            className={
                "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold capitalize " +
                cls
            }
        >
            {value || "-"}
        </span>
    );
}

function normalizeList(res, keys = ["items", "rows", "data"]) {
    if (Array.isArray(res)) return res;
    for (const key of keys) {
        if (Array.isArray(res?.[key])) return res[key];
    }
    return [];
}

function formatDate(value) {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
}

function getErrorText(row) {
    if (Array.isArray(row?.errors) && row.errors.length) return row.errors.join(", ");
    if (Array.isArray(row?.errorMessages) && row.errorMessages.length) {
        return row.errorMessages.join(", ");
    }
    if (typeof row?.error === "string") return row.error;
    return "-";
}

function getRowStatus(row) {
    return (
        row?.status ||
        row?.validationStatus ||
        (row?.isValid === true ? "valid" : row?.isValid === false ? "invalid" : "")
    );
}

function getRowData(row) {
    return row?.raw || row?.data || row?.row || row;
}

function downloadSampleFile() {
    const csvRows = [
        [
            "sku",
            "barcode",
            "name",
            "slug",
            "category_slug",
            "brand_slug",
            "unit_symbol",
            "weight_value",
            "weight_unit_symbol",
            "mrp",
            "selling_price",
            "tax_percent",
            "status",
            "is_active",
            "short_description",
            "description",
            "image_url",
            "currency",
        ],
        [
            "WS-SKU-0001",
            "8901000000037",
            "Apple - Shimla",
            "apple-shimla",
            "fruits-vegetables",
            "freshfarm",
            "kg",
            "1",
            "kg",
            "180",
            "149",
            "0",
            "active",
            "TRUE",
            "Apple - Shimla",
            "Apple - Shimla - quality packed for daily use.",
            "",
            "INR",
        ],
    ];

    const csv = csvRows
        .map((row) =>
            row
                .map((cell) => {
                    const text = String(cell ?? "");
                    return `"${text.replace(/"/g, '""')}"`;
                })
                .join(",")
        )
        .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "catalog_import_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default function AdminCatalogImport() {
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseId, setWarehouseId] = useState("");

    const [activeTab, setActiveTab] = useState("assign");

    const [file, setFile] = useState(null);
    const [jobId, setJobId] = useState("");
    const [uploadMeta, setUploadMeta] = useState(null);
    const [preview, setPreview] = useState(null);

    const [mastersLoading, setMastersLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [committing, setCommitting] = useState(false);

    const [assignSearch, setAssignSearch] = useState("");
    const [assignPage, setAssignPage] = useState(1);
    const [assignLimit, setAssignLimit] = useState(20);
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignSaving, setAssignSaving] = useState(false);
    const [rowSavingId, setRowSavingId] = useState("");
    const [assignRows, setAssignRows] = useState([]);
    const [assignTotal, setAssignTotal] = useState(0);
    const [selectedVariantIds, setSelectedVariantIds] = useState([]);

    const [toast, setToast] = useState("");
    const [error, setError] = useState("");

    const navigate = useNavigate();

    const loadWarehouses = async () => {
        try {
            setMastersLoading(true);
            const res = await warehouseService.listWarehouses({ page: 1, limit: 200 });
            const items = normalizeList(res);
            setWarehouses(items);
        } catch (e) {
            setError(e?.message || "Failed to load warehouses");
        } finally {
            setMastersLoading(false);
        }
    };

    const loadAssignmentProducts = async (
        targetWarehouseId = warehouseId,
        targetPage = assignPage,
        targetSearch = assignSearch
    ) => {
        if (!targetWarehouseId) {
            setAssignRows([]);
            setAssignTotal(0);
            setSelectedVariantIds([]);
            return;
        }

        try {
            setAssignLoading(true);
            setError("");

            const res = await productsService.listWarehouseAssignmentProducts({
                warehouseId: targetWarehouseId,
                q: targetSearch,
                page: targetPage,
                limit: assignLimit,
            });

            const raw = res?.data ?? res ?? {};
            const items = normalizeList(raw, ["items", "rows", "data"]);
            const total = Number(raw?.total || items.length || 0);

            const flattened = items.flatMap((product) => {
                const hasVariants =
                    Array.isArray(product?.variants) && product.variants.length > 0;

                const variants = hasVariants
                    ? product.variants
                    : [
                        {
                            id:
                                product?.productVariantId ||
                                product?.variantId ||
                                product?.defaultVariantId ||
                                `product-${product.id}`,
                            variantName:
                                product?.variantName ||
                                product?.defaultVariant?.variantName ||
                                product?.name ||
                                "Default",
                            sku: product?.sku || "",
                            barcode: product?.barcode || "",
                            mrp: product?.mrp ?? null,
                            sellingPrice: product?.sellingPrice ?? null,
                            assigned: Boolean(product?.assigned),
                            isFallbackProductRow: true,
                        },
                    ];

                return variants.map((variant) => ({
                    rowId: `${product.id}-${variant.id}`,
                    productId: product.id,
                    variantId: String(variant.id),
                    productName: product.name || "-",
                    variantName:
                        variant?.variantName ||
                        variant?.variant_name ||
                        variant?.name ||
                        (variant?.isFallbackProductRow ? "No Variant" : "Default"),
                    sku: variant?.variantSku || variant?.sku || product?.sku || "",
                    barcode: variant?.barcode || product?.barcode || "",
                    brandName: product?.brandName || "-",
                    categoryName: product?.categoryName || "-",
                    mrp: variant?.mrp ?? product?.mrp ?? null,
                    sellingPrice: variant?.sellingPrice ?? product?.sellingPrice ?? null,
                    assigned: Boolean(variant?.assigned),
                    isFallbackProductRow: Boolean(variant?.isFallbackProductRow),
                }));
            });

            setAssignRows(flattened);
            setAssignTotal(total || flattened.length);
            setSelectedVariantIds([]);
        } catch (e) {
            setError(e?.message || "Failed to load master products");
        } finally {
            setAssignLoading(false);
        }
    };
    const reloadAssignmentProductsFresh = async (
        targetWarehouseId = warehouseId,
        targetPage = assignPage,
        targetSearch = assignSearch
    ) => {
        if (!targetWarehouseId) return;

        try {
            setAssignLoading(true);
            setError("");

            const res = await productsService.listWarehouseAssignmentProducts({
                warehouseId: targetWarehouseId,
                q: targetSearch,
                page: targetPage,
                limit: assignLimit,
            });

            const raw = res?.data ?? res ?? {};
            const items = normalizeList(raw, ["items", "rows", "data"]);
            const total = Number(raw?.total || items.length || 0);

            const flattened = items.flatMap((product) => {
                const hasVariants =
                    Array.isArray(product?.variants) && product.variants.length > 0;

                const variants = hasVariants
                    ? product.variants
                    : [
                        {
                            id:
                                product?.productVariantId ||
                                product?.variantId ||
                                product?.defaultVariantId ||
                                `product-${product.id}`,
                            variantName:
                                product?.variantName ||
                                product?.defaultVariant?.variantName ||
                                product?.name ||
                                "Default",
                            sku: product?.sku || "",
                            barcode: product?.barcode || "",
                            mrp: product?.mrp ?? null,
                            sellingPrice: product?.sellingPrice ?? null,
                            assigned: Boolean(product?.assigned),
                            isFallbackProductRow: true,
                        },
                    ];

                return variants.map((variant) => ({
                    rowId: `${product.id}-${variant.id}`,
                    productId: product.id,
                    variantId: String(variant.id),
                    productName: product.name || "-",
                    variantName:
                        variant?.variantName ||
                        variant?.variant_name ||
                        variant?.name ||
                        (variant?.isFallbackProductRow ? "No Variant" : "Default"),
                    sku: variant?.variantSku || variant?.sku || product?.sku || "",
                    barcode: variant?.barcode || product?.barcode || "",
                    brandName: product?.brandName || "-",
                    categoryName: product?.categoryName || "-",
                    mrp: variant?.mrp ?? product?.mrp ?? null,
                    sellingPrice: variant?.sellingPrice ?? product?.sellingPrice ?? null,
                    assigned: Boolean(variant?.assigned),
                    isFallbackProductRow: Boolean(variant?.isFallbackProductRow),
                }));
            });

            setAssignRows(flattened);
            setAssignTotal(total || flattened.length);
            setSelectedVariantIds([]);
        } catch (e) {
            setError(e?.message || "Failed to load master products");
        } finally {
            setAssignLoading(false);
        }
    };

    const toggleVariant = (variantId) => {
        const nextId = String(variantId);
        setSelectedVariantIds((prev) =>
            prev.includes(nextId) ? prev.filter((id) => id !== nextId) : [...prev, nextId]
        );
    };

    const toggleAllVisible = () => {
        const visibleIds = assignRows.map((item) => String(item.variantId)).filter(Boolean);
        const allSelected =
            visibleIds.length > 0 &&
            visibleIds.every((id) => selectedVariantIds.includes(id));

        if (allSelected) {
            setSelectedVariantIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
            return;
        }

        setSelectedVariantIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    };

    const buildAssignmentPayload = (
        nextSelectedVariantIds = selectedVariantIds,
        visibleRows = assignRows
    ) => {
        const currentRowsMap = new Map();

        [...assignRows, ...visibleRows].forEach((row) => {
            const variantId = String(row?.variantId || "").trim();
            const productId = String(row?.productId || "").trim();

            if (variantId) currentRowsMap.set(variantId, row);
            if (productId) currentRowsMap.set(productId, row);
            if (productId) currentRowsMap.set(`product-${productId}`, row);
        });

        const variantIds = [];
        const productIds = [];
        const selectedRows = [];

        nextSelectedVariantIds.forEach((id) => {
            const safeId = String(id || "").trim();
            if (!safeId) return;

            const row = currentRowsMap.get(safeId);

            if (row) {
                selectedRows.push(row);
            }

            if (row?.isFallbackProductRow || safeId.startsWith("product-")) {
                const productId = row?.productId || safeId.replace(/^product-/, "");
                if (productId) productIds.push(String(productId));
                return;
            }

            variantIds.push(safeId);
        });

        const visibleVariantIds = visibleRows
            .map((row) => String(row?.variantId || ""))
            .filter((id) => id && !id.startsWith("product-"));

        const productNames = [
            ...new Set(
                selectedRows
                    .map((row) =>
                        row?.productName ||
                        row?.name ||
                        row?.product_name ||
                        row?.title ||
                        row?.label ||
                        row?.sku
                    )
                    .filter(Boolean)
                    .map((name) => String(name).trim())
                    .filter(Boolean)
            ),
        ];

        const selectedWarehouse = warehouses.find(
            (w) => String(w?.id) === String(warehouseId)
        );

        const warehouseName =
            selectedWarehouse?.name ||
            selectedWarehouse?.warehouseName ||
            selectedWarehouse?.warehouse_name ||
            selectedWarehouse?.label ||
            selectedWarehouse?.code ||
            "";

        return {
            variantIds: Array.from(new Set(variantIds)),
            productIds: Array.from(new Set(productIds)),
            visibleVariantIds: Array.from(new Set(visibleVariantIds)),
            productNames,
            warehouseName,
        };
    };

    const runBulkAssignment = async (mode) => {
        if (!warehouseId) {
            setError("Please select warehouse first");
            return;
        }

        const selectedRows = assignRows.filter((row) =>
            selectedVariantIds.includes(String(row?.variantId || ""))
        );

        const actionRows = selectedRows.filter((row) =>
            mode === "assign" ? !row?.assigned : row?.assigned
        );

        if (!actionRows.length) {
            setToast(
                mode === "assign"
                    ? "Select at least one not-assigned variant to assign"
                    : "Select at least one assigned variant to unassign"
            );
            return;
        }

        try {
            setAssignSaving(true);
            setError("");

            const payload = buildAssignmentPayload(
                actionRows.map((row) => String(row.variantId)),
                actionRows
            );

            const res = await productsService.commitWarehouseAssignment({
                warehouseId,
                mode,
                ...payload,
            });

            setToast(
                res?.message ||
                (mode === "assign"
                    ? "Selected products assigned successfully"
                    : "Selected products unassigned successfully")
            );
            setSelectedVariantIds([]);
            await reloadAssignmentProductsFresh(warehouseId, assignPage, assignSearch);
        } catch (e) {
            const message =
                e?.message ||
                (mode === "assign"
                    ? "Failed to assign selected products"
                    : "Failed to unassign selected products");

            setError("");
            await appAlert(message, {
                title: mode === "assign" ? "Assign failed" : "Unassign blocked",
                danger: true,
                confirmText: "OK",
            });
        } finally {
            setAssignSaving(false);
        }
    };

    const handleSingleAssignment = async (item) => {
        if (!warehouseId) {
            setError("Please select warehouse first");
            return;
        }

        const safeId = String(item?.variantId || "").trim();
        if (!safeId) return;

        const rowKey = item?.rowId || safeId;
        const isAssigned = Boolean(item?.assigned);
        const mode = isAssigned ? "unassign" : "assign";

        try {
            setRowSavingId(rowKey);
            setError("");

            const payload = buildAssignmentPayload([safeId], [item]);

            const res = await productsService.commitWarehouseAssignment({
                warehouseId,
                mode,
                ...payload,
            });

            setToast(
                res?.message ||
                (isAssigned
                    ? "Product unassigned successfully"
                    : "Product assigned successfully")
            );
            setSelectedVariantIds((prev) => prev.filter((id) => String(id) !== safeId));
            await reloadAssignmentProductsFresh(warehouseId, assignPage, assignSearch);
        } catch (e) {
            const message =
                e?.message ||
                (isAssigned
                    ? "Failed to unassign product"
                    : "Failed to assign product");

            setError("");
            await appAlert(message, {
                title: isAssigned ? "Unassign blocked" : "Assign failed",
                danger: true,
                confirmText: "OK",
            });
        } finally {
            setRowSavingId("");
        }
    };

    useEffect(() => {
        loadWarehouses();
    }, []);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(""), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    useEffect(() => {
        if (activeTab !== "assign") return;
        if (!warehouseId) return;
        loadAssignmentProducts(warehouseId, assignPage, assignSearch);
    }, [activeTab, warehouseId, assignPage, assignLimit]);

    useEffect(() => {
        if (activeTab !== "assign") return;
        const t = setTimeout(() => {
            if (!warehouseId) return;
            setAssignPage(1);
            loadAssignmentProducts(warehouseId, 1, assignSearch);
        }, 400);

        return () => clearTimeout(t);
    }, [assignSearch, activeTab, warehouseId]);

    const warehouseOptions = useMemo(() => {
        return warehouses.map((w) => ({
            value: w.id,
            label: w.name || w.code || w.id,
        }));
    }, [warehouses]);

    const assignTotalPages = useMemo(() => {
        return Math.max(1, Math.ceil(Number(assignTotal || 0) / Number(assignLimit || 20)));
    }, [assignTotal, assignLimit]);

    const assignPageNumbers = useMemo(() => {
        const totalPages = assignTotalPages;
        const current = assignPage;
        const pages = [];

        const start = Math.max(1, current - 2);
        const end = Math.min(totalPages, current + 2);

        for (let i = start; i <= end; i += 1) {
            pages.push(i);
        }

        return pages;
    }, [assignPage, assignTotalPages]);

    const selectedAssignRows = useMemo(() => {
        const selected = new Set(selectedVariantIds.map(String));
        return assignRows.filter((row) => selected.has(String(row?.variantId || "")));
    }, [assignRows, selectedVariantIds]);

    const selectedAssignableCount = useMemo(
        () => selectedAssignRows.filter((row) => !row?.assigned).length,
        [selectedAssignRows]
    );

    const selectedUnassignableCount = useMemo(
        () => selectedAssignRows.filter((row) => row?.assigned).length,
        [selectedAssignRows]
    );

    const previewRows = useMemo(() => {
        if (!preview) return [];
        return normalizeList(preview, ["rows", "items", "data"]);
    }, [preview]);

    const stats = useMemo(() => {
        const totalRows =
            Number(preview?.totalRows) ||
            Number(preview?.rowCount) ||
            previewRows.length ||
            0;

        const validRows =
            Number(preview?.validRows) ||
            Number(preview?.validCount) ||
            previewRows.filter((row) => String(getRowStatus(row)).toLowerCase() === "valid").length;

        const invalidRows =
            Number(preview?.invalidRows) ||
            Number(preview?.invalidCount) ||
            previewRows.filter((row) => String(getRowStatus(row)).toLowerCase() === "invalid").length;

        return {
            totalRows,
            validRows,
            invalidRows,
            uploadedAt: uploadMeta?.createdAt || preview?.createdAt || "",
        };
    }, [preview, previewRows, uploadMeta]);

    const handleUpload = async () => {
        if (!warehouseId) {
            setToast("Please select warehouse first");
            return;
        }

        if (!file) {
            setToast("Please choose an Excel file first");
            return;
        }

        try {
            setUploading(true);
            setError("");
            setPreview(null);
            setJobId("");
            setUploadMeta(null);

            const res = await importService.uploadExcel(file);

            const nextJobId =
                res?.jobId ||
                res?.data?.jobId ||
                res?.job?.id ||
                res?.data?.job?.id ||
                "";

            if (!nextJobId) {
                throw new Error("jobId not received from upload API");
            }

            setJobId(nextJobId);
            setUploadMeta(res);
            setToast("Excel uploaded successfully");
        } catch (e) {
            setError(e?.message || "Failed to upload Excel");
        } finally {
            setUploading(false);
        }
    };

    const handlePreview = async () => {
        if (!jobId) {
            setToast("Please upload file first");
            return;
        }

        try {
            setPreviewLoading(true);
            setError("");

            const res = await importService.previewImport(jobId);
            setPreview(res);
            setToast("Preview loaded successfully");
        } catch (e) {
            setError(e?.message || "Failed to load preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleCommit = async () => {
        if (!warehouseId) {
            setToast("Please select warehouse first");
            return;
        }

        if (!jobId) {
            setToast("Please upload file first");
            return;
        }

        if (!preview) {
            setToast("Please preview import before commit");
            return;
        }

        try {
            setCommitting(true);
            setError("");

            const res = await importService.commitImport(jobId, {
                warehouseId,
            });

            setToast(res?.message || "Catalog imported successfully");
            setFile(null);
            setJobId("");
            setUploadMeta(null);
            setPreview(null);
            loadWarehouses();
        } catch (e) {
            setError(e?.message || "Failed to commit import");
        } finally {
            setCommitting(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setJobId("");
        setUploadMeta(null);
        setPreview(null);
        setError("");
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 rounded-[12px] border border-[#f2c6cc] bg-white px-3 py-2 text-[13px] font-semibold text-[#d86a78] hover:bg-[#fff3f5]"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </button>

                    <h1 className="text-[30px] font-bold text-slate-800">
                        Assign Products to Warehouse
                    </h1>
                </div>

                <div className="flex flex-wrap gap-3">
                    <ActionButton variant="soft" onClick={loadWarehouses} disabled={mastersLoading}>
                        <RefreshCcw size={16} />
                        {mastersLoading ? "Refreshing..." : "Refresh"}
                    </ActionButton>

                    <ActionButton variant="soft" onClick={downloadSampleFile}>
                        <Download size={16} />
                        Download Sample
                    </ActionButton>
                </div>
            </div>

            <div className="flex flex-wrap gap-3">
                <ActionButton
                    variant={activeTab === "assign" ? "primary" : "soft"}
                    onClick={() => setActiveTab("assign")}
                >
                    <FileSpreadsheet size={16} />
                    Assign Master Products
                </ActionButton>

                {/* <ActionButton
                    variant={activeTab === "excel" ? "primary" : "soft"}
                    onClick={() => setActiveTab("excel")}
                >
                    <Upload size={16} />
                    Excel Import
                </ActionButton> */}
            </div>

            {activeTab === "excel" && (
                <>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <SummaryCard
                            icon={FileSpreadsheet}
                            title="Total Rows"
                            value={stats.totalRows}
                            subtitle="Previewed rows"
                        />
                        <SummaryCard
                            icon={CheckCircle2}
                            title="Valid Rows"
                            value={stats.validRows}
                            subtitle="Ready to import"
                        />
                        <SummaryCard
                            icon={AlertCircle}
                            title="Invalid Rows"
                            value={stats.invalidRows}
                            subtitle="Need fixing in excel"
                        />
                    </div>

                    <Card className="p-5">
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[320px_1fr_auto_auto_auto] xl:items-end">
                            <div>
                                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                    Warehouse
                                </div>
                                <SelectLike
                                    value={warehouseId}
                                    onChange={(value) => {
                                        setWarehouseId(value);
                                        setSelectedVariantIds([]);
                                        setAssignRows([]);
                                        setAssignTotal(0);
                                        setAssignPage(1);
                                    }}
                                    placeholder="Select warehouse"
                                    options={warehouseOptions}
                                />
                            </div>

                            <div>
                                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                    Excel File
                                </div>
                                <label className="flex h-[44px] cursor-pointer items-center gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-4 text-[13px] text-slate-700 transition hover:bg-[#fffafb]">
                                    <Upload size={16} className="text-[#d86a78]" />
                                    <span className="truncate">
                                        {file ? file.name : "Choose .xlsx / .xls / .csv file"}
                                    </span>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls,.csv"
                                        className="hidden"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    />
                                </label>
                            </div>

                            <ActionButton
                                onClick={handleUpload}
                                disabled={uploading || !warehouseId || !file}
                            >
                                <Upload size={16} />
                                {uploading ? "Uploading..." : "Upload"}
                            </ActionButton>

                            <ActionButton
                                variant="soft"
                                onClick={handlePreview}
                                disabled={previewLoading || !jobId}
                            >
                                <Eye size={16} />
                                {previewLoading ? "Loading..." : "Preview"}
                            </ActionButton>

                            <ActionButton
                                variant="dark"
                                onClick={handleCommit}
                                disabled={committing || !jobId || !warehouseId || !preview}
                            >
                                <Send size={16} />
                                {committing ? "Importing..." : "Commit Import"}
                            </ActionButton>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <ActionButton variant="soft" onClick={handleReset}>
                                Reset
                            </ActionButton>

                            {warehouseId ? (
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-2 text-[12px] font-semibold text-[#d86a78]">
                                    <Warehouse size={14} />
                                    Selected Warehouse:{" "}
                                    {warehouseOptions.find((x) => x.value === warehouseId)?.label || warehouseId}
                                </div>
                            ) : null}

                            {jobId ? (
                                <div className="rounded-full bg-slate-100 px-3 py-2 text-[12px] font-semibold text-slate-700">
                                    Job ID: {jobId}
                                </div>
                            ) : null}

                            {stats.uploadedAt ? (
                                <div className="rounded-full bg-slate-100 px-3 py-2 text-[12px] font-semibold text-slate-700">
                                    Uploaded: {formatDate(stats.uploadedAt)}
                                </div>
                            ) : null}
                        </div>
                    </Card>

                    <Card className="overflow-hidden">
                        <div className="flex flex-col gap-3 border-b border-[#f4d8dc] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <div className="text-[18px] font-bold text-slate-800">Preview Rows</div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <StatusPill value={preview ? "preview" : "pending"} />
                            </div>
                        </div>

                        {!jobId ? (
                            <div className="p-5 text-[13px] text-slate-500"></div>
                        ) : previewLoading ? (
                            <div className="p-5 text-[13px] text-slate-500">Loading preview...</div>
                        ) : !preview ? (
                            <div className="p-5 text-[13px] text-slate-500"></div>
                        ) : previewRows.length === 0 ? (
                            <div className="p-5 text-[13px] text-slate-500">No preview rows found.</div>
                        ) : (
                            <div className="overflow-auto">
                                <table className="w-full min-w-[2200px]">
                                    <thead className="bg-[#fff7f8] text-left text-[12px] uppercase tracking-[0.12em] text-slate-500">
                                        <tr>
                                            <th className="px-5 py-3">Row</th>
                                            <th className="px-5 py-3">Status</th>
                                            <th className="px-5 py-3">SKU</th>
                                            <th className="px-5 py-3">Barcode</th>
                                            <th className="px-5 py-3">Name</th>
                                            <th className="px-5 py-3">Slug</th>
                                            <th className="px-5 py-3">Category</th>
                                            <th className="px-5 py-3">Brand</th>
                                            <th className="px-5 py-3">Unit Symbol</th>
                                            <th className="px-5 py-3">Weight Value</th>
                                            <th className="px-5 py-3">Weight Unit</th>
                                            <th className="px-5 py-3">MRP</th>
                                            <th className="px-5 py-3">Selling Price</th>
                                            <th className="px-5 py-3">Tax %</th>
                                            <th className="px-5 py-3">Status Value</th>
                                            <th className="px-5 py-3">Is Active</th>
                                            <th className="px-5 py-3">Short Description</th>
                                            <th className="px-5 py-3">Description</th>
                                            <th className="px-5 py-3">Currency</th>
                                            <th className="px-5 py-3">Image URL</th>
                                            <th className="px-5 py-3">Errors</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, index) => {
                                            const data = getRowData(row);
                                            const status = getRowStatus(row) || "valid";

                                            return (
                                                <tr
                                                    key={row?.rowNo || row?.rowNumber || row?.id || index}
                                                    className="border-t border-[#f8dde1] text-[13px] text-slate-700 align-top"
                                                >
                                                    <td className="px-5 py-4">
                                                        {row?.rowNo || row?.rowNumber || index + 1}
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        <StatusPill value={status} />
                                                    </td>
                                                    <td className="px-5 py-4 font-semibold">{data?.sku || "-"}</td>
                                                    <td className="px-5 py-4">{data?.barcode || "-"}</td>
                                                    <td className="px-5 py-4">{data?.name || "-"}</td>
                                                    <td className="px-5 py-4">{data?.slug || "-"}</td>
                                                    <td className="px-5 py-4">{data?.category_slug || "-"}</td>
                                                    <td className="px-5 py-4">{data?.brand_slug || "-"}</td>
                                                    <td className="px-5 py-4">{data?.unit_symbol || "-"}</td>
                                                    <td className="px-5 py-4">{data?.weight_value || "-"}</td>
                                                    <td className="px-5 py-4">{data?.weight_unit_symbol || "-"}</td>
                                                    <td className="px-5 py-4">{data?.mrp || "-"}</td>
                                                    <td className="px-5 py-4">{data?.selling_price || "-"}</td>
                                                    <td className="px-5 py-4">{data?.tax_percent || "-"}</td>
                                                    <td className="px-5 py-4">{data?.status || "-"}</td>
                                                    <td className="px-5 py-4">
                                                        {data?.is_active === true
                                                            ? "TRUE"
                                                            : data?.is_active === false
                                                                ? "FALSE"
                                                                : "-"}
                                                    </td>
                                                    <td className="px-5 py-4 min-w-[220px]">
                                                        {data?.short_description || "-"}
                                                    </td>
                                                    <td className="px-5 py-4 min-w-[260px]">
                                                        {data?.description || "-"}
                                                    </td>
                                                    <td className="px-5 py-4">{data?.currency || "-"}</td>
                                                    <td className="px-5 py-4">{data?.image_url || "-"}</td>
                                                    <td className="px-5 py-4 text-rose-600">
                                                        {getErrorText(row)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </>
            )}

            {activeTab === "assign" && (
                <>
                    <Card className="sticky top-0 z-30 bg-white/95 p-5 backdrop-blur">
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[260px_1fr_auto] xl:items-end">
                            <div>
                                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                    Warehouse
                                </div>
                                <SelectLike
                                    value={warehouseId}
                                    onChange={(value) => {
                                        setWarehouseId(value);
                                        setAssignPage(1);
                                        setAssignRows([]);
                                        setAssignTotal(0);
                                        setSelectedVariantIds([]);
                                    }}
                                    placeholder="Select warehouse"
                                    options={warehouseOptions}
                                />
                            </div>

                            <div>
                                <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                                    Search Products
                                </div>
                                <div className="relative h-[44px]">
                                    <Search
                                        size={16}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                    />
                                    <input
                                        value={assignSearch}
                                        onChange={(e) => setAssignSearch(e.target.value)}
                                        placeholder="Search by name / sku / barcode"
                                        className="h-full w-full rounded-[12px] border border-[#f2c6cc] bg-white pl-9 pr-4 text-[13px] text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <ActionButton
                                    variant="soft"
                                    onClick={() => loadAssignmentProducts(warehouseId, assignPage, assignSearch)}
                                    disabled={!warehouseId || assignLoading}
                                >
                                    <RefreshCcw size={16} />
                                    {assignLoading ? "Refreshing..." : "Refresh"}
                                </ActionButton>

                                <ActionButton
                                    onClick={() => runBulkAssignment("assign")}
                                    disabled={!warehouseId || assignSaving || selectedAssignableCount <= 0}
                                >
                                    <Send size={16} />
                                    {assignSaving ? "Saving..." : `Assign Selected (${selectedAssignableCount})`}
                                </ActionButton>

                                <ActionButton
                                    variant="soft"
                                    onClick={() => runBulkAssignment("unassign")}
                                    disabled={!warehouseId || assignSaving || selectedUnassignableCount <= 0}
                                >
                                    <AlertCircle size={16} />
                                    {assignSaving ? "Saving..." : `Unassign Selected (${selectedUnassignableCount})`}
                                </ActionButton>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            {warehouseId ? (
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#fff4f6] px-3 py-2 text-[12px] font-semibold text-[#d86a78]">
                                    <Warehouse size={14} />
                                    Selected Warehouse:{" "}
                                    {warehouseOptions.find((x) => x.value === warehouseId)?.label || warehouseId}
                                </div>
                            ) : null}

                            <div className="rounded-full bg-slate-100 px-3 py-2 text-[12px] font-semibold text-slate-700">
                                Selected Rows: {selectedVariantIds.length}
                            </div>
                        </div>
                    </Card>

                    <Card className="flex max-h-[calc(100vh-315px)] min-h-[430px] flex-col overflow-hidden">
                        <div className="shrink-0 flex flex-col gap-3 border-b border-[#f4d8dc] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <div className="text-[18px] font-bold text-slate-800">Master Products</div>
                                <div className="mt-1 text-[12px] text-slate-500">
                                    Checkbox only selects rows. Status and action update only after Assign or Unassign succeeds.
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <StatusPill value={warehouseId ? "preview" : "pending"} />
                                <div className="rounded-full bg-slate-100 px-3 py-2 text-[12px] font-semibold text-slate-700">
                                    Total: {assignTotal}
                                </div>
                            </div>
                        </div>

                        {!warehouseId ? (
                            <div className="p-5 text-[13px] text-slate-500">
                                Please select a warehouse first.
                            </div>
                        ) : assignLoading ? (
                            <div className="p-5 text-[13px] text-slate-500">Loading master products...</div>
                        ) : assignRows.length === 0 ? (
                            <div className="p-5 text-[13px] text-slate-500">No master products found.</div>
                        ) : (
                            <>
                                <div className="min-h-0 flex-1 overflow-auto">
                                    <table className="w-full min-w-[1350px]">
                                        <thead className="sticky top-0 z-10 bg-[#fff7f8] text-left text-[12px] uppercase tracking-[0.12em] text-slate-500">
                                            <tr>
                                                <th className="px-5 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            assignRows.length > 0 &&
                                                            assignRows.every((item) =>
                                                                selectedVariantIds.includes(String(item.variantId))
                                                            )
                                                        }
                                                        onChange={toggleAllVisible}
                                                    />
                                                </th>
                                                <th className="px-5 py-3">Product</th>
                                                <th className="px-5 py-3">Variant</th>
                                                <th className="px-5 py-3">SKU</th>
                                                <th className="px-5 py-3">Barcode</th>
                                                <th className="px-5 py-3">Brand</th>
                                                <th className="px-5 py-3">Category</th>
                                                <th className="px-5 py-3">MRP</th>
                                                <th className="px-5 py-3">Selling Price</th>
                                                <th className="px-5 py-3">Status</th>
                                                <th className="px-5 py-3">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignRows.map((item) => {
                                                const checked = selectedVariantIds.includes(String(item.variantId));
                                                const isAssigned = Boolean(item?.assigned);

                                                return (
                                                    <tr
                                                        key={item.rowId}
                                                        className="border-t border-[#f8dde1] text-[13px] text-slate-700"
                                                    >
                                                        <td className="px-5 py-4">
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleVariant(item.variantId)}
                                                            />
                                                        </td>
                                                        <td className="px-5 py-4 font-semibold">
                                                            {item?.productName || "-"}
                                                        </td>
                                                        <td className="px-5 py-4">
                                                            {item?.variantName || "Default"}
                                                        </td>
                                                        <td className="px-5 py-4">{item?.sku || "-"}</td>
                                                        <td className="px-5 py-4">{item?.barcode || "-"}</td>
                                                        <td className="px-5 py-4">{item?.brandName || "-"}</td>
                                                        <td className="px-5 py-4">{item?.categoryName || "-"}</td>
                                                        <td className="px-5 py-4">
                                                            {item?.mrp != null ? `₹ ${Number(item.mrp).toLocaleString()}` : "-"}
                                                        </td>
                                                        <td className="px-5 py-4">
                                                            {item?.sellingPrice != null
                                                                ? `₹ ${Number(item.sellingPrice).toLocaleString()}`
                                                                : "-"}
                                                        </td>
                                                        <td className="px-5 py-4">
                                                            <StatusPill value={isAssigned ? "assigned" : "not assigned"} />
                                                        </td>
                                                        <td className="px-5 py-4">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSingleAssignment(item)}
                                                                disabled={assignSaving || rowSavingId === (item.rowId || String(item.variantId))}
                                                                className={
                                                                    "inline-flex min-w-[92px] items-center justify-center rounded-[10px] px-3 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 " +
                                                                    (isAssigned
                                                                        ? "border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                                                                        : "bg-[#d86a78] text-white hover:bg-[#c95b69]")
                                                                }
                                                            >
                                                                {rowSavingId === (item.rowId || String(item.variantId))
                                                                    ? "Saving..."
                                                                    : isAssigned
                                                                        ? "Unassign"
                                                                        : "Assign"}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="shrink-0 flex flex-col gap-3 border-t border-[#f4d8dc] bg-white px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                                    <div className="text-[12px] text-slate-500">
                                        Showing page{" "}
                                        <span className="font-semibold text-slate-700">{assignPage}</span> of{" "}
                                        <span className="font-semibold text-slate-700">{assignTotalPages}</span>
                                        {" "}· Total {assignTotal}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <select
                                            value={assignLimit}
                                            onChange={(e) => {
                                                setAssignPage(1);
                                                setAssignLimit(Number(e.target.value));
                                            }}
                                            className="h-[38px] rounded-[10px] border border-[#f2c6cc] bg-white px-3 text-[12px] font-semibold text-slate-700 outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                                        >
                                            <option value={20}>20 / page</option>
                                            <option value={50}>50 / page</option>
                                            <option value={100}>100 / page</option>
                                            <option value={500}>500 / page</option>
                                        </select>

                                        <ActionButton
                                            variant="soft"
                                            disabled={assignPage <= 1}
                                            onClick={() => setAssignPage((p) => Math.max(1, p - 1))}
                                        >
                                            Prev
                                        </ActionButton>

                                        {assignPage > 3 ? (
                                            <>
                                                <ActionButton variant="soft" onClick={() => setAssignPage(1)}>
                                                    1
                                                </ActionButton>
                                                <span className="px-1 text-[12px] text-slate-400">...</span>
                                            </>
                                        ) : null}

                                        {assignPageNumbers.map((pageNo) => (
                                            <button
                                                key={pageNo}
                                                type="button"
                                                onClick={() => setAssignPage(pageNo)}
                                                className={
                                                    "h-[38px] min-w-[38px] rounded-[10px] px-3 text-[12px] font-semibold transition " +
                                                    (pageNo === assignPage
                                                        ? "bg-[#d86a78] text-white"
                                                        : "border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff3f5]")
                                                }
                                            >
                                                {pageNo}
                                            </button>
                                        ))}

                                        {assignPage < assignTotalPages - 2 ? (
                                            <>
                                                <span className="px-1 text-[12px] text-slate-400">...</span>
                                                <ActionButton variant="soft" onClick={() => setAssignPage(assignTotalPages)}>
                                                    {assignTotalPages}
                                                </ActionButton>
                                            </>
                                        ) : null}

                                        <ActionButton
                                            variant="soft"
                                            disabled={assignPage >= assignTotalPages}
                                            onClick={() => setAssignPage((p) => Math.min(assignTotalPages, p + 1))}
                                        >
                                            Next
                                        </ActionButton>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                </>
            )}

            {error ? (
                <Card className="border-rose-200 bg-rose-50 p-4">
                    <div className="text-[13px] font-semibold text-rose-700">{error}</div>
                </Card>
            ) : null}

            {toast ? (
                <div className="fixed bottom-5 right-5 z-[999] rounded-[14px] bg-slate-900 px-4 py-3 text-[13px] font-semibold text-white shadow-lg">
                    {toast}
                </div>
            ) : null}
        </div>
    );
}